import React, { useState, useEffect, useMemo } from 'react';
import clsx from 'clsx';
import { useDiscordGuilds } from '../hooks/usePermissions.js';
import { useConversationConfig, useConversationConfigUpdate } from '../hooks/useConversationConfig.js';
import type { SupportedBotName } from '@discord-bot-platform/service-client';
import type { ConversationConfig } from '../lib/api/domains/conversations.js';
import {
  Card,
  Button,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Input,
  Badge
} from '@randompedestrian07/ui';
import { Settings, Save, Loader2, Users } from 'lucide-react';

const POLICY_OPTIONS = [
  { id: 'open', label: 'Open' },
  { id: 'intruder_detection', label: 'Intruder Detection' },
  { id: 'whitelist', label: 'Whitelist' },
] as const;

type PolicyType = (typeof POLICY_OPTIONS)[number]['id'];

export const ConversationConfigSection: React.FC<{ botName: SupportedBotName; title?: string }> = ({ botName, title }) => {
  const { data: guildsData, isLoading: guildsLoading } = useDiscordGuilds();
  const guilds = useMemo(() => guildsData?.guilds ?? [], [guildsData]);
  const [selectedGuild, setSelectedGuild] = useState<string>('');
  const [form, setForm] = useState<ConversationConfig | null>(null);
  const [status, setStatus] = useState<{ type: 'success' | 'error' | '', text: string }>({ type: '', text: '' });

  useEffect(() => {
    if (!selectedGuild && guilds.length > 0) {
      setSelectedGuild(guilds[0].id);
    }
  }, [guilds, selectedGuild]);

  const { data: config, isLoading: configLoading } = useConversationConfig(selectedGuild, botName, !!selectedGuild);
  const updateConfig = useConversationConfigUpdate();

  useEffect(() => {
    if (config) {
      setForm(config);
    }
  }, [config]);

  const isSaving = updateConfig.isPending;
  const effectiveTitle = title ?? 'Conversation Settings';

  const canSave = useMemo(() => {
    return !!selectedGuild && !!form && !isSaving;
  }, [selectedGuild, form, isSaving]);

  const onSave = async () => {
    if (!form || !selectedGuild) return;
    setStatus({ type: '', text: '' });
    try {
      await updateConfig.mutateAsync({
        guildId: selectedGuild,
        botName,
        config: {
          policyType: form.policyType,
          windowSize: form.windowSize,
          maxChars: form.maxChars,
          multiUserMode: form.multiUserMode,
        },
      });
      setStatus({ type: 'success', text: 'Settings saved successfully' });
      setTimeout(() => setStatus({ type: '', text: '' }), 3000);
    } catch {
      setStatus({ type: 'error', text: 'Failed to save settings' });
    }
  };

  const updateNumber = (value: string, fallback: number) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed < 0) return fallback;
    return parsed;
  };

  return (
    <Card className="bg-panel-strong/40 border-stroke p-6 space-y-6 backdrop-blur-md rounded-2xl">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-accent/10 rounded-lg">
            <Settings className="text-accent" size={20} />
          </div>
          <h2 className="text-xl font-bold text-text tracking-tight">{effectiveTitle}</h2>
        </div>
        <div className="flex items-center gap-3">
          <div className="w-[200px]">
            <Select
              value={selectedGuild}
              onValueChange={setSelectedGuild}
            >
              <SelectTrigger className="bg-panel border-stroke">
                <SelectValue placeholder="Select server" />
              </SelectTrigger>
              <SelectContent>
                {guilds.map((guild) => (
                  <SelectItem key={guild.id} value={guild.id}>
                    {guild.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            variant="ghost"
            onClick={onSave}
            disabled={!canSave}
            className="!bg-accent !text-black hover:!bg-accent/90 shadow-lg shadow-accent/20 !border-none px-6"
          >
            {isSaving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Save className="mr-2" size={16} />}
            Save
          </Button>
        </div>
      </div>

      {configLoading && (
        <div className="flex items-center justify-center py-12 gap-3 text-muted">
          <Loader2 className="animate-spin" size={20} />
          <span>Syncing with server...</span>
        </div>
      )}

      {form && !configLoading && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="text-[10px] font-bold uppercase tracking-widest text-muted/60 px-1">Policy Type</label>
            <Select
              value={form.policyType}
              onValueChange={(val) => setForm(prev => prev ? { ...prev, policyType: val as PolicyType } : null)}
            >
              <SelectTrigger className="bg-panel border-stroke h-11">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {POLICY_OPTIONS.map((opt) => (
                  <SelectItem key={opt.id} value={opt.id}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label className="text-[10px] font-bold uppercase tracking-widest text-muted/60 px-1">Window Size (messages)</label>
            <Input
              type="number"
              min={1}
              className="bg-panel border-stroke h-11"
              value={form.windowSize}
              onChange={(e) => setForm(prev => prev ? { ...prev, windowSize: updateNumber(e.target.value, prev.windowSize) } : null)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-[10px] font-bold uppercase tracking-widest text-muted/60 px-1">Max Characters (per turn)</label>
            <Input
              type="number"
              min={1}
              placeholder="No limit"
              className="bg-panel border-stroke h-11"
              value={form.maxChars ?? ''}
              onChange={(e) => setForm(prev => prev ? { ...prev, maxChars: e.target.value === '' ? undefined : updateNumber(e.target.value, prev.maxChars ?? 1) } : null)}
            />
          </div>

          <div className="flex items-center justify-between bg-panel/30 border border-stroke rounded-xl px-4 py-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/5 rounded-lg">
                <Users size={18} className="text-muted" />
              </div>
              <div>
                <div className="text-sm font-semibold text-text">Multi-user Mode</div>
                <div className="text-[10px] text-muted opacity-80 uppercase tracking-tighter">Shared conversation threads</div>
              </div>
            </div>
            <button
              className={clsx(
                "relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200",
                form.multiUserMode ? "bg-accent" : "bg-panel-strong border border-stroke"
              )}
              onClick={() => setForm(prev => (prev ? { ...prev, multiUserMode: !prev.multiUserMode } : prev))}
            >
              <span className={clsx(
                "inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200",
                form.multiUserMode ? "translate-x-6" : "translate-x-1"
              )} />
            </button>
          </div>
        </div>
      )}

      {status.text && (
        <div className={clsx(
          "text-xs font-medium px-4 py-2 rounded-lg text-center animate-in fade-in slide-in-from-bottom-2",
          status.type === 'success' ? "bg-green/10 text-green" : "bg-warning/10 text-warning"
        )}>
          {status.text}
        </div>
      )}
    </Card>
  );
};
